<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { INK_FILTER } from '@vp/render';
import { canvasDark } from '../theme';
import { CoreTypes, type ObjectRecord } from '@vp/shared-types';
import type { Rect } from '@vp/core';
import { live, ui } from '../editor/bridge';
import { t } from '../i18n';
import { settings } from '../platform';

/**
 * Canvas minimap for navigation: object silhouettes, the current view as a frame;
 * click or drag to move the view, wheel to zoom.
 */
const W = 208;
const H = 136;
const base = ref<HTMLCanvasElement | null>(null);
const top = ref<HTMLCanvasElement | null>(null);
const empty = ref(true);
let world: Rect | null = null;
let baseRaf = 0;
let topRaf = 0;
let lastBase = 0;
let camUnsub: (() => void) | null = null;
let camFor: unknown = null;
let dragging = false;

function dpr(): number {
  return window.devicePixelRatio || 1;
}

/** Keep the backing store in sync with the device pixel ratio (it can change at runtime). */
function fit(c: HTMLCanvasElement): number {
  const k = dpr();
  const w = Math.round(W * k);
  const h = Math.round(H * k);
  if (c.width !== w || c.height !== h) {
    c.width = w;
    c.height = h;
  }
  return k;
}

function computeWorld(): Rect | null {
  const ed = live.editor;
  const r = live.renderer;
  if (!ed || !r) return null;
  const content = ed.scene.index.totalBounds();
  const view = r.camera.visibleRect();
  const b = content
    ? { x: Math.min(content.x, view.x), y: Math.min(content.y, view.y), w: 0, h: 0, x1: Math.max(content.x + content.w, view.x + view.w), y1: Math.max(content.y + content.h, view.y + view.h) }
    : { x: view.x, y: view.y, w: 0, h: 0, x1: view.x + view.w, y1: view.y + view.h };
  let w = b.x1 - b.x;
  let h = b.y1 - b.y;
  // keep the minimap aspect ratio
  const ar = W / H;
  if (w / h > ar) {
    const nh = w / ar;
    b.y -= (nh - h) / 2;
    h = nh;
  } else {
    const nw = h * ar;
    b.x -= (nw - w) / 2;
    w = nw;
  }
  const pad = Math.max(w, h) * 0.06;
  return { x: b.x - pad, y: b.y - pad, w: w + pad * 2, h: h + pad * 2 };
}

function scheduleBase(): void {
  if (baseRaf) return;
  const wait = Math.max(0, 250 - (performance.now() - lastBase));
  baseRaf = window.setTimeout(() => {
    baseRaf = 0;
    lastBase = performance.now();
    drawBase();
    drawTop();
  }, wait) as unknown as number;
}

function scheduleTop(): void {
  if (topRaf) return;
  topRaf = requestAnimationFrame(() => {
    topRaf = 0;
    const prev = world;
    const next = computeWorld();
    // the view left the mapped area: everything must be re-mapped
    if (!prev || !next || Math.abs(next.w - prev.w) > prev.w * 0.01 || Math.abs(next.x - prev.x) > prev.w * 0.01 || Math.abs(next.y - prev.y) > prev.h * 0.01) scheduleBase();
    drawTop();
  });
}

function follow(): void {
  const r = live.renderer;
  if (r && r !== camFor) {
    camUnsub?.();
    camUnsub = r.camera.onChange(scheduleTop);
    camFor = r;
  }
}

function drawBase(): void {
  const c = base.value;
  const ed = live.editor;
  follow();
  if (!c || !ed) return;
  world = computeWorld();
  empty.value = ed.doc.size === 0;
  const k = fit(c);
  const ctx = c.getContext('2d')!;
  ctx.setTransform(k, 0, 0, k, 0, 0);
  ctx.clearRect(0, 0, W, H);
  if (!world) return;
  const s = W / world.w;
  // canvas background so light objects stand out
  ctx.fillStyle = '#eef2f6';
  ctx.fillRect(0, 0, W, H);
  drawFrames(ctx, s);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const o of ed.doc.allObjects()) {
    if (o.type === CoreTypes.group || ed.scene.isFolded(o.id)) continue;
    if (o.type === CoreTypes.pen || o.type === CoreTypes.path || o.type === CoreTypes.line || o.type === CoreTypes.connector) {
      // strokes stay strokes on the map
      const pts = (o.type === CoreTypes.connector ? o.props['path'] : o.props['points']) as number[] | undefined;
      if (!Array.isArray(pts) || pts.length < 4) continue;
      const c = Math.cos(o.rotation);
      const sn = Math.sin(o.rotation);
      const cx = o.x + o.w / 2;
      const cy = o.y + o.h / 2;
      const step = Math.max(2, Math.floor(pts.length / 2 / 60) * 2);
      ctx.beginPath();
      for (let i = 0; i < pts.length; i += step) {
        const lx = pts[i] - o.w / 2;
        const ly = pts[i + 1] - o.h / 2;
        const x = (cx + lx * c - ly * sn - world.x) * s;
        const y = (cy + lx * sn + ly * c - world.y) * s;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      if (o.props['closed'] === true) ctx.closePath();
      ctx.strokeStyle = o.style.stroke?.color ?? '#64748b';
      ctx.lineWidth = Math.max(1.4, (o.style.stroke?.width ?? 1) * s);
      ctx.stroke();
      continue;
    }
    if (o.type === CoreTypes.frame) continue; // drawn first, below everything
    drawBox(ctx, o, s);
  }
}

/** Map silhouette of an object: tiny things stay visible, light fills get an outline, text becomes bars. */
function drawBox(ctx: CanvasRenderingContext2D, o: ObjectRecord, s: number): void {
  if (!world) return;
  const MIN = 2.5;
  let w = o.w * s;
  let h = o.h * s;
  let x = (o.x - world.x) * s;
  let y = (o.y - world.y) * s;
  if (w < MIN) {
    x -= (MIN - w) / 2;
    w = MIN;
  }
  if (h < MIN) {
    y -= (MIN - h) / 2;
    h = MIN;
  }
  if (o.type === CoreTypes.text && o.style.fill.kind === 'none') {
    ctx.fillStyle = '#64748b';
    const bar = Math.max(1.5, Math.min(h, 3));
    ctx.fillRect(x, y + (h - bar) / 2, w, bar);
    return;
  }
  const f = o.style.fill;
  // patterns read as their background on the map
  const fill = f.kind === 'solid' ? f.color.slice(0, 7) : f.kind === 'none' || !f.background ? null : f.background.slice(0, 7);
  const light = !fill || isLight(fill);
  if (fill || f.kind === 'none') {
    ctx.fillStyle = fill ?? '#ffffff';
    ctx.fillRect(x, y, w, h);
  }
  if (light) {
    ctx.strokeStyle = o.style.stroke?.color ?? '#94a3b8';
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, Math.max(1, w - 1), Math.max(1, h - 1));
  }
}

function isLight(hex: string): boolean {
  const n = parseInt(hex.slice(1, 7), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return 0.299 * r + 0.587 * g + 0.114 * b > 225;
}

function drawFrames(ctx: CanvasRenderingContext2D, s: number): void {
  const ed = live.editor;
  if (!ed || !world) return;
  for (const o of ed.doc.allObjects()) {
    if (o.type !== CoreTypes.frame) continue;
    const x = (o.x - world.x) * s;
    const y = (o.y - world.y) * s;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(x, y, Math.max(2, o.w * s), Math.max(2, o.h * s));
    ctx.strokeStyle = '#94a3b8';
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, Math.max(1, o.w * s - 1), Math.max(1, o.h * s - 1));
  }
}

function drawTop(): void {
  const c = top.value;
  const r = live.renderer;
  if (!c || !r || !world) return;
  const k = fit(c);
  const ctx = c.getContext('2d')!;
  ctx.setTransform(k, 0, 0, k, 0, 0);
  ctx.clearRect(0, 0, W, H);
  const s = W / world.w;
  const v = r.camera.visibleRect();
  const x = (v.x - world.x) * s;
  const y = (v.y - world.y) * s;
  const w = v.w * s;
  const h = v.h * s;
  // dim everything outside the view
  ctx.fillStyle = 'rgba(15, 23, 42, 0.06)';
  ctx.fillRect(0, 0, W, H);
  ctx.clearRect(x, y, w, h);
  ctx.strokeStyle = settings.accent;
  ctx.lineWidth = 1.5;
  ctx.strokeRect(x + 0.75, y + 0.75, Math.max(2, w - 1.5), Math.max(2, h - 1.5));
}

function moveTo(e: PointerEvent): void {
  const r = live.renderer;
  if (!r || !world || !top.value) return;
  const rect = top.value.getBoundingClientRect();
  const s = world.w / W;
  r.camera.set({ cx: world.x + (e.clientX - rect.left) * s, cy: world.y + (e.clientY - rect.top) * s });
}

function down(e: PointerEvent): void {
  dragging = true;
  live.editor?.beginViewGesture();
  (e.target as Element).setPointerCapture(e.pointerId);
  moveTo(e);
}

function move(e: PointerEvent): void {
  if (dragging) moveTo(e);
}

function up(): void {
  dragging = false;
  live.editor?.endViewGesture();
  scheduleBase();
}

function wheel(e: WheelEvent): void {
  e.preventDefault();
  live.editor?.zoomBy(Math.exp(-e.deltaY * 0.0015));
}

onMounted(() => {
  scheduleBase();
});

onBeforeUnmount(() => {
  camUnsub?.();
  clearTimeout(baseRaf);
  cancelAnimationFrame(topRaf);
});

watch(() => ui.docRev, scheduleBase);
watch(() => live.session?.id, scheduleBase);
</script>

<template>
  <div v-show="settings.minimap && !empty" class="float card minimap" role="img" :aria-label="t('minimap.title')">
    <canvas ref="base" :style="{ width: W + 'px', height: H + 'px', filter: canvasDark ? INK_FILTER : undefined }"></canvas>
    <canvas
      ref="top"
      class="top"
      :style="{ width: W + 'px', height: H + 'px' }"
      :title="t('minimap.hint')"
      @mousedown.prevent
      @pointerdown="down"
      @pointermove="move"
      @pointerup="up"
      @pointercancel="up"
      @wheel="wheel"
    ></canvas>
  </div>
</template>

<style scoped>
.minimap {
  right: 12px;
  bottom: 60px;
  width: 216px;
  height: 144px;
  padding: 4px;
  border-radius: var(--radius-panel);
  overflow: hidden;
}
canvas {
  position: absolute;
  left: 4px;
  top: 4px;
  display: block;
  border-radius: var(--radius-panel);
}
.top {
  cursor: pointer;
  touch-action: none;
}
</style>
